import { Box, Skeleton, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useRef } from "react";
import {
    DotLegalButton,
    DotLegalCheckbox,
    DotLegalChip,
    DotLegalEditIconButton,
    DotLegalHeader,
    DotLegalPageHeader,
    usePaginationDataMapping,
} from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import DotLegalDeleteDialogWrapper from "../../common/components/dotLegalDeleteDialog/DotLegalDeleteDialogWrapper";
import { useActivitiesBatchDelete } from "./ActivitiesBatchDelete.hooks";
import { getRowFilterKey, MAX_BULK_SELECTION } from "@common/bulkSelection/bulkSelection.utils";
import BulkActionsMenu from "@common/bulkSelection/BulkActionsMenu";
import BulkSelectionAlert from "@common/bulkSelection/BulkSelectionAlert";
import BulkSelectionFooter from "@common/bulkSelection/BulkSelectionFooter";
import { useActivitiesInlineEdit } from "./ActivitiesInlineEdit.hooks";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import FilterBar from "../../common/components/filterBar/FilterBar";
import DotLegalPaper from "../../common/components/dotLegalPaper/DotLegalPaper";
import { useTranslation } from "../../localization/useTranslation";
import { buildActivityDisplayData, parseActivityDisplayData, useActivitiesDataMapping } from "./Activities.hooks";
import overdueTasksWave from "../overdueTasksWave.svg?url";
import plannedTasksWave from "../plannedTasksWave.svg?url";
import completedTasksWave from "../completedTasksWave.svg?url";
import totalTasksWave from "../totalTasksWave.svg?url";
import DeleteAnnualWheelActivityDialog from "../deleteAnnualWheelActivtyDialog/DeleteAnnualWheelActivityDialog";
import { useNavMenuDataMapping } from "../../main/NavMenu/NavMenu.hooks";
import ActivitiesListOverview from "./ActivitiesListOverview";
import { TaskStateEnum } from "../AnnualWheel.types";
import TableDisplayFields from "@common/components/tableDisplayFields/TableDisplayFields";
import { ActivityFieldGroupingOrder } from "@common/components/filterPopover/FilterPopover.types";
import SavedViewsMenu from "../../common/components/savedViews/SavedViewsMenu";
import ExcelExportButton from "@common/components/excelExportButton/ExcelExportButton";
import { FilterPage } from "../../common/pageView/PageView.types";
import { useAnnualWheelChipStyles, useAnnualWheelStyles } from "../AnnualWheel.styles";
import { useActivitiesFilter } from "./ActivitiesFilter.hooks";
import { defaultActivitiesPageSize } from "./ActivitiesSearch.types";
import { SortDirection } from "../../contract/ContractFilter.types";
import StatusTimelineFieldConfig, { StatusTimelineConfig } from "./components/statusTimeline/StatusTimelineFieldConfig";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function Activities() {
    const theme = useTheme();
    const styles = useAnnualWheelStyles();
    const { translateString } = useTranslation();
    const { overDueTaskCountColor, plannedTaskCountColor, completedTaskCountColor, totalTaskCounterColor } = useAnnualWheelChipStyles();

    const {
        isLoading,
        data,
        refetchData,
        openActivity,
        openTemplates,
        applyResponsibleToCachedActivities,
        permissions,
        onBoxCounterClick,
        hasData,
        activityForDeletion,
        setActivityForDeletion,
        onFilterBarUpdate,
        searchText,
        searchedFilterData,
        onSearchChange,
        setSortOrder,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        currentViewId,
        filterData,
        displayFields,
        timelineConfig,
        rawDisplayData,
        savedViewQuery,
        savedView,
        isSavedView,
        hasFilterChanged,
        pageViewHasDisplayChanged,
        setDisplayData,
        setFilterData,
        tableFields,
        onDownloadExcelReportClick,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = useActivitiesDataMapping();

    const timelineConfigRef = useRef<StatusTimelineConfig | null>(timelineConfig ?? null);
    // timelineConfig is re-read from session storage on every render, so it is compared by value: only a view switch or a
    // save may replace the settings the column pane is still editing.
    const savedTimelineConfigJson = JSON.stringify(timelineConfig ?? null);
    const lastSavedTimelineConfigJson = useRef(savedTimelineConfigJson);
    if (savedTimelineConfigJson !== lastSavedTimelineConfigJson.current) {
        lastSavedTimelineConfigJson.current = savedTimelineConfigJson;
        timelineConfigRef.current = timelineConfig ?? timelineConfigRef.current;
    }

    const { activityFilterOptions } = useActivitiesFilter(filterData);
    const { refetchMenuNotifications } = useNavMenuDataMapping();
    const { addonsFeatures } = usePlanContext();
    const { setPage, getPagination } = usePaginationDataMapping(true);
    const { to, from } = getPagination(data?.totalCount ?? 0, data?.activities ?? [], false, data?.pageSize ?? defaultActivitiesPageSize);

    const batchDelete = useActivitiesBatchDelete({
        onBatchDeleteSuccess: () => {
            refetchData();
            refetchMenuNotifications().then();
        },
        // The search box sits outside the filter data, but it narrows the rows just the same
        rowFilterKey: getRowFilterKey(searchedFilterData),
    });

    const inlineEdit = useActivitiesInlineEdit({
        activities: data?.activities,
        displayFields,
        catalogueOrder: tableFields.map((field) => field.id),
        onResponsibleSaved: applyResponsibleToCachedActivities,
        onEditModeClosed: refetchData,
    });

    const searchedStatus = filterData?.status?.value ?? [];

    return (
        <React.Fragment>
            <DotLegalPageHeader
                breadCrumbs={[{ name: translateString("activities") }]}
                rightComponent={
                    permissions.annualWheelPermission.create ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <DotLegalButton id={"create-annual-wheel-activity-from-template"} buttonType={"secondary"} size="medium" onClick={openTemplates}>
                                {translateString("createFromTemplate")}
                            </DotLegalButton>
                            <DotLegalButton id={"create-annual-wheel-activity"} buttonType={"primary"} size="medium" onClick={() => openActivity(undefined)}>
                                {translateString("createActivity")}
                            </DotLegalButton>
                        </Box>
                    ) : undefined
                }
            />
            <Grid
                container
                spacing={2}
                sx={() => ({
                    "& .MuiChip-label": {
                        fontWeight: "bold",
                    },
                })}
            >
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <Box>
                        <DotLegalPaper
                            sx={{
                                backgroundImage: `url("${overdueTasksWave}")`,
                                outline: searchedStatus.includes(TaskStateEnum.Overdue.toString()) ? theme.palette.primary.dark + " solid" : "",
                                ...styles.taskCounterPaper,
                            }}
                            clickableRows
                            onClick={() => onBoxCounterClick(TaskStateEnum.Overdue)}
                        >
                            <DotLegalHeader headerStyle={"medium"} center>
                                {translateString("overdueActivities")}
                            </DotLegalHeader>
                            {!isLoading ? (
                                <DotLegalChip alternativeColor={overDueTaskCountColor()} value={data!.overdueTaskCount!.toString()} />
                            ) : (
                                <Skeleton>
                                    <DotLegalChip alternativeColor={totalTaskCounterColor()} value={"0"} />
                                </Skeleton>
                            )}
                        </DotLegalPaper>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <DotLegalPaper
                        sx={{
                            backgroundImage: `url("${plannedTasksWave}")`,
                            outline: searchedStatus.includes(TaskStateEnum.Ready.toString()) ? theme.palette.primary.dark + " solid" : "",
                            ...styles.taskCounterPaper,
                        }}
                        clickableRows
                        onClick={() => onBoxCounterClick(TaskStateEnum.Ready)}
                    >
                        <Box
                            sx={() => ({
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            })}
                        >
                            <DotLegalHeader headerStyle={"medium"} center>
                                {translateString("plannedActivities")}
                            </DotLegalHeader>
                            {!isLoading ? (
                                <DotLegalChip alternativeColor={plannedTaskCountColor()} value={data!.plannedTaskCount!.toString()} />
                            ) : (
                                <Skeleton>
                                    <DotLegalChip alternativeColor={totalTaskCounterColor()} value={"0"} />
                                </Skeleton>
                            )}
                        </Box>
                    </DotLegalPaper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <DotLegalPaper
                        sx={{
                            backgroundImage: `url("${completedTasksWave}")`,
                            outline: searchedStatus.includes(TaskStateEnum.Completed.toString()) ? theme.palette.primary.dark + " solid" : "",
                            ...styles.taskCounterPaper,
                        }}
                        clickableRows
                        onClick={() => onBoxCounterClick(TaskStateEnum.Completed)}
                    >
                        <DotLegalHeader headerStyle={"medium"} center>
                            {translateString("completedActivities")}
                        </DotLegalHeader>
                        {!isLoading ? (
                            <DotLegalChip alternativeColor={completedTaskCountColor()} value={data!.completedTaskCount!.toString()} />
                        ) : (
                            <Skeleton>
                                <DotLegalChip alternativeColor={totalTaskCounterColor()} value={"0"} />
                            </Skeleton>
                        )}
                    </DotLegalPaper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <DotLegalPaper sx={{ backgroundImage: `url("${totalTasksWave}")`, ...styles.taskCounterPaper }}>
                        <DotLegalHeader headerStyle={"medium"} center>
                            {translateString("totalTasksForYear")}
                        </DotLegalHeader>
                        {!isLoading ? (
                            <DotLegalChip alternativeColor={totalTaskCounterColor()} value={data!.totalTaskCount!.toString()} />
                        ) : (
                            <Skeleton>
                                <DotLegalChip alternativeColor={totalTaskCounterColor()} value={"0"} />
                            </Skeleton>
                        )}
                    </DotLegalPaper>
                </Grid>
            </Grid>

            <Box sx={{ marginTop: theme.spacing(2), height: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
                <TablePagePaper removePadding>
                    <Box sx={{ padding: 2, height: "100%", display: "flex", flexDirection: "column", flex: 1 }}>
                        <ListPageToolbar
                            savedViewsMenu={
                                <SavedViewsMenu
                                    currentViewId={currentViewId}
                                    parentIsLoading={isLoading || savedViewQuery.isLoading}
                                    currentFilterData={filterData}
                                    currentDisplayData={rawDisplayData}
                                    onViewChange={onChangeView}
                                    onViewSaved={onViewSaved}
                                    onViewDeleted={onViewDeleted}
                                    filterPage={FilterPage.Activities}
                                    hasFilterOrDisplayDataChanged={hasFilterChanged || pageViewHasDisplayChanged}
                                />
                            }
                            leftContent={
                                <FilterBar
                                    isLoading={isLoading}
                                    filterData={activityFilterOptions}
                                    fetchFilterData
                                    onMenuClose={onFilterBarUpdate}
                                    onRemove={onFilterBarUpdate}
                                    pinnedFilterIds={pinnedFilterIds}
                                    onPinnedFilterIdsChange={setPinnedFilterIds}
                                    onClearFilter={hasFilterChanged ? () => setFilterData(undefined) : undefined}
                                    clearFilterText={hasFilterChanged ? (isSavedView ? "savedView" : "defaultView") : undefined}
                                    groupingOrder={ActivityFieldGroupingOrder}
                                    searchValue={searchText}
                                    searchLabel={translateString("searchActivitiesPlaceholder")}
                                    onSearchChange={onSearchChange}
                                />
                            }
                            rightContent={
                                <>
                                    {savedView && (
                                        <TableDisplayFields
                                            selectedFields={displayFields}
                                            onSave={(columns) => setDisplayData(buildActivityDisplayData(columns, timelineConfigRef.current))}
                                            options={tableFields}
                                            hasDisplayChanged={pageViewHasDisplayChanged}
                                            isCustomView={isSavedView}
                                            groupingOrder={ActivityFieldGroupingOrder}
                                            savedViewFields={parseActivityDisplayData(savedView.displayData).fields}
                                            renderSelectedField={(fieldId, fieldName) => {
                                                if (fieldId === "status-timeline") {
                                                    return <StatusTimelineFieldConfig fieldName={fieldName} configRef={timelineConfigRef} />;
                                                }
                                                return undefined;
                                            }}
                                        />
                                    )}
                                    {savedView && (
                                        <ExcelExportButton
                                            onClick={onDownloadExcelReportClick}
                                            isDisabled={isLoading || savedViewQuery.isLoading}
                                            isPlanLocked={!addonsFeatures.customLists}
                                            planLockedText={translateString("upgradeToCustomList")}
                                        />
                                    )}
                                </>
                            }
                            bulkActions={
                                permissions.annualWheelPermission.edit && (
                                    <BulkActionsMenu
                                        items={[
                                            {
                                                menuItem: translateString("editActivities"),
                                                onClick: inlineEdit.editResponsible,
                                                disabled: batchDelete.isSelectionMode || inlineEdit.isEditMode,
                                            },
                                            {
                                                menuItem: translateString("deleteActivities"),
                                                onClick: () => batchDelete.setIsSelectionMode(true),
                                                disabled: batchDelete.isSelectionMode || inlineEdit.isEditMode,
                                            },
                                        ]}
                                    />
                                )
                            }
                        />
                        {batchDelete.isSelectionMode && (
                            <BulkSelectionAlert
                                limitText={translateString("batchDeleteActivitiesLimitInfo", {
                                    selected: batchDelete.selectedCount,
                                    max: MAX_BULK_SELECTION,
                                })}
                                exceedsLimit={batchDelete.exceedsLimit}
                                selectedCount={batchDelete.selectedCount}
                                onDeselectAllClick={batchDelete.clearSelection}
                                marginTop={2}
                            />
                        )}
                        <ActivitiesListOverview
                            setActivityForDeletion={setActivityForDeletion}
                            activities={data?.activities}
                            isLoading={isLoading}
                            hasData={hasData}
                            onOpenActivity={(activityId) => openActivity(activityId)}
                            selectedDisplayFields={inlineEdit.effectiveDisplayFields}
                            sortByColumn={filterData?.sortByColumn}
                            sortDirection={filterData?.sortDirection === SortDirection.DESC ? "desc" : "asc"}
                            onSortChange={setSortOrder}
                            timelineConfig={timelineConfig}
                            paginationProps={{
                                calculatedPage: filterData?.pageNumber ?? 1,
                                count: data?.totalCount ?? 0,
                                from,
                                to,
                                noOfPages: data?.totalPages ?? 1,
                                setPage: (pageNumber) => {
                                    setPage(pageNumber);
                                    setFilterData({ ...filterData, pageNumber: pageNumber });
                                },
                                labelOf: translateString("labelOf"),
                            }}
                            isSelectionMode={batchDelete.isSelectionMode}
                            selectedIds={batchDelete.selectedIds}
                            onCheckboxClick={batchDelete.onCheckboxClick}
                            onSelectAllOnPage={batchDelete.onSelectAllOnPage}
                            getHeaderCheckBoxState={batchDelete.getHeaderCheckBoxState}
                            editingField={inlineEdit.editingField}
                            selectableResponsibles={inlineEdit.selectableResponsibles}
                            responsiblesLoading={inlineEdit.responsiblesLoading}
                            getResponsibleName={inlineEdit.getResponsibleName}
                            onResponsibleChange={inlineEdit.onResponsibleChange}
                        />
                        {inlineEdit.isEditMode && (
                            <Box sx={{ display: "flex", justifyContent: "end", mt: 2 }}>
                                <DotLegalButton buttonType="secondary" onClick={inlineEdit.exitEditMode}>
                                    {translateString("close")}
                                </DotLegalButton>
                            </Box>
                        )}
                        {batchDelete.isSelectionMode && (
                            <BulkSelectionFooter
                                isDeleteMode
                                onCloseClick={batchDelete.exitSelectionMode}
                                onDeleteClick={() => batchDelete.setShowBatchDeleteDialog(true)}
                                deleteDisabled={!batchDelete.canDelete}
                                deleteText={
                                    batchDelete.selectedCount === 0
                                        ? translateString("deleteActivities")
                                        : translateString("deleteActivitiesWithCount", { count: batchDelete.selectedCount })
                                }
                            />
                        )}
                    </Box>
                </TablePagePaper>
            </Box>

            {activityForDeletion && (
                <DeleteAnnualWheelActivityDialog
                    activityId={activityForDeletion!.id}
                    onFrameworkControl={activityForDeletion?.onFrameworkControl}
                    activityName={activityForDeletion.name}
                    onCloseDialog={() => {
                        setActivityForDeletion(undefined);
                    }}
                    onDeleteClick={() => {
                        refetchData();
                        refetchMenuNotifications().then();
                        setActivityForDeletion(undefined);
                    }}
                    hardDelete={activityForDeletion?.deleted}
                />
            )}
            {batchDelete.showBatchDeleteDialog && (
                <DotLegalDeleteDialogWrapper
                    open
                    inProgress={batchDelete.isBatchDeleting}
                    itemForDeletion={translateString("countActivities", { count: batchDelete.selectedCount }).toLowerCase()}
                    onDialogClose={() => {
                        batchDelete.setShowBatchDeleteDialog(false);
                        batchDelete.setHardDelete(false);
                    }}
                    onOkClick={batchDelete.onBatchDeleteConfirm}
                    additionalWarning={
                        <Trans
                            i18nKey={"deleteActivitiesWarning"}
                            components={{
                                strong: <Box component="span" sx={{ fontWeight: "bold" }} />,
                            }}
                        />
                    }
                >
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: -2 }}>
                        <DotLegalCheckbox
                            checked={batchDelete.hardDelete}
                            onChange={(checked) => batchDelete.setHardDelete(checked)}
                            label={translateString("permanentlyDeleteActivities")}
                        />
                    </Box>
                </DotLegalDeleteDialogWrapper>
            )}
        </React.Fragment>
    );
}

export default Activities;
